'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Callout, CheckboxField, Tabs } from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import {
  EMAIL_METHOD,
  PHONE_METHOD,
  SignInContactFields,
  useSignInContactForm,
  type SignInContactErrors,
} from '../components/SignInContactFields';
import { SignupShell } from '../components/SignupShell';
import { telehealthSegments, termsSegments } from '../components/signup-consents';
import { ensureSignupIdentityStyle } from '../components/signup-identity-style';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useDisclosureConsentLink } from '../hooks/useDisclosureConsentLink';
import { useSignupApi } from '../hooks/useSignupApi';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import { sendFailureStatus } from '../services/send-failure-status';
import { useSignupFlowStore, type SignupSignInContact } from '../stores/signup-flow-store';
import { EarlierAnswersGate } from './EarlierAnswersGate';
import { ResumedSignInSkip } from './ResumedSignInSkip';

const FIELD_TEST_IDS = { phone: 'signup-account-phone', email: 'signup-account-email' } as const;

interface ConsentErrors {
  terms?: string;
  telehealth?: string;
}

function AccountStep() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const { t } = useTranslation(['signup', 'common']);
  const title = t('account.title');
  const headingRef = useStepHeading(title);
  const api = useSignupApi();
  const { goToStep } = useSignupNavigation();
  const setSignInContact = useSignupFlowStore((state) => state.setSignInContact);
  const setOtpResendAfterSeconds = useSignupFlowStore((state) => state.setOtpResendAfterSeconds);
  const disclosureLink = useDisclosureConsentLink();

  const contactForm = useSignInContactForm();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [telehealthAccepted, setTelehealthAccepted] = useState(false);
  const [errors, setErrors] = useState<ConsentErrors>({});
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A ref, not the state above, guards re-entry: two presses in one tick both
  // see the state before it updates.
  const submittingRef = useRef(false);
  // A send that completes after the visitor left this step, signed out or
  // opened another sponsor's link must not store a contact or move the flow on.
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);
  ensureSignupStepStyle();
  ensureSignupIdentityStyle();

  const clearError = (field: keyof ConsentErrors) =>
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));

  /** Where the code goes, or null with the field errors when something is missing. */
  const validate = (): {
    contactErrors: SignInContactErrors;
    consentErrors: ConsentErrors;
    contact: SignupSignInContact | null;
  } => {
    const read = contactForm.readContact();
    const consentErrors: ConsentErrors = {};
    if (!termsAccepted) consentErrors.terms = t('account.consentRequired');
    if (!telehealthAccepted) consentErrors.telehealth = t('account.consentRequired');
    return { contactErrors: read.errors, consentErrors, contact: read.contact };
  };

  // A new and an existing account get the same answer here: the code screen
  // signs either one in, so nothing tells a visitor whether an address is taken.
  // A reply is dropped only when the flow restarted while it was in flight (a
  // different sponsor link). A same-flow URL change, such as a language
  // switch writing ?lng=, keeps it, as the other steps' epoch checks do.
  const sendCode = async (contact: SignupSignInContact) => {
    const epochAtSend = useSignupFlowStore.getState().flowEpoch;
    const sent = await api.initiateOtp(contact);
    if (!liveRef.current || useSignupFlowStore.getState().flowEpoch !== epochAtSend) return;
    setSignInContact(contact);
    setOtpResendAfterSeconds(sent.resend_available_in_seconds ?? null);
    goToStep('code');
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current) return;
    const checked = validate();
    contactForm.setErrors(checked.contactErrors);
    setErrors(checked.consentErrors);
    if (Object.values(checked.consentErrors).some(Boolean) || checked.contact === null) return;
    const contact = checked.contact;
    submittingRef.current = true;
    setSubmitting(true);
    setStatus('');
    try {
      await sendCode(contact);
    } catch (error) {
      if (!liveRef.current) return;
      if (contactForm.showFieldRejection(error)) setErrors({});
      else setStatus(sendFailureStatus(t, error));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <SignupShell>
      <form className="signup-step" noValidate onSubmit={onSubmit}>
        <PageTitle title={title} description={t('account.description')} headingRef={headingRef} />
        <Tabs
          fullWidth
          items={[
            { key: PHONE_METHOD, label: t('account.tabs.phone') },
            { key: EMAIL_METHOD, label: t('account.tabs.email') },
          ]}
          activeKey={contactForm.method}
          onChange={(key) => {
            contactForm.setMethod(key === EMAIL_METHOD ? EMAIL_METHOD : PHONE_METHOD);
            setStatus('');
          }}
          accessibilityLabel={t('account.methodLabel')}
          actionId="signup.account-screen-method.change"
          data-testid="signup-account-method"
        />
        <SignInContactFields form={contactForm} testIds={FIELD_TEST_IDS} />
        <CheckboxField
          labelSegments={termsSegments(t)}
          checked={termsAccepted}
          onChange={(next) => {
            setTermsAccepted(next);
            clearError('terms');
          }}
          errorText={errors.terms}
          actionId="signup.account-screen-consent.change"
          data-testid="signup-account-consent-terms"
        />
        <CheckboxField
          labelSegments={telehealthSegments(t, disclosureLink)}
          checked={telehealthAccepted}
          onChange={(next) => {
            setTelehealthAccepted(next);
            clearError('telehealth');
          }}
          errorText={errors.telehealth}
          actionId="signup.account-screen-consent.change"
          data-testid="signup-account-consent-telehealth"
        />
        {/* The one status announcer for this step: always mounted, its text
            changes. The visible callout is hidden from assistive technology so
            the message is not announced twice. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {status}
        </p>
        {status ? (
          <div aria-hidden="true">
            <Callout tone="error" body={status} />
          </div>
        ) : null}
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            htmlType="submit"
            state={submitting ? 'Loading' : 'Default'}
            actionId="signup.account-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </form>
    </SignupShell>
  );
}

/**
 * `/account/`: creates the account with a one-time code sent to a cell
 * phone (the default) or an email (v9 1.4 and 1.4a, code-only).
 */
export function AccountStepScreen() {
  return (
    <EarlierAnswersGate step="account">
      <ResumedSignInSkip>
        <AccountStep />
      </ResumedSignInSkip>
    </EarlierAnswersGate>
  );
}
